import { Box } from "@mui/material";
import { DotLegalButton } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../../localization/useTranslation";
import SubProcessorComponent from "../subProcessorComponent/SubProcessorComponent";
import React from "react";
import { DataProcessor } from "../subProcessorComponent/SubProcessorComponent.types";
import { useDataProcessorsStepStyles } from "../DataProcessorsStep.styles";
import {
    DataCategoryDataTypes,
    SelectableDataCategoryItem,
    SelectableDocumentItem,
    SelectableItem,
    SelectableLegalUnitsItem,
    SubProcessor,
} from "../../../ProcessingActivity.types";
import { ValidationError } from "../../../../common/validationError";
import { DotLegalSelectOption } from "../../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import DotLegalRemovableItems, { IDotLegalRemovableItem } from "@common/components/dotLegalRemovableItems/DotLegalRemovableItems";

export interface IDataProcessorsStep {
    subProcessors: Array<SubProcessor>;
    subDataProcessorLink?: string;
    onLinkChange: (link: string, dataProcessorId: string, index: number) => void;
    onAddNewSubprocessorClick: (isLink: boolean) => void;
    readonly: boolean;
    canAddNewSubDataProcessor: boolean;
    groupEntityIsDataProcessor: boolean;
    processingActivityName?: string;
    isReadOnly: boolean;
    dataProcessor: DataProcessor;
    isLegalEntityLoading: boolean;
    documentsLoading: boolean;
    documentsData: Array<SelectableDocumentItem> | undefined;
    legalEntityData?: SelectableLegalUnitsItem[];
    isTransferBasisLoading: boolean;
    transferBasisData: SelectableItem[] | undefined;
    validationErrors: Array<ValidationError>;
    onSubProcessorTransferBasisChange?: (transferBasis: string | null, dataProcessorId: string, index: number) => void;
    onRemoveSubDataProcessor?: (dataProcessorId: string, subprocessorIndex: number) => void;
    onSubProcessorAgreementChange?: (hasAgreement: boolean, dataProcessorId: string, subprocessorInde: number) => void;
    onSubDataProcessorChange?: (
        processorId: string | null,
        dataProcessorId: string,
        subprocessorInde: number,
        legalEntity: SelectableLegalUnitsItem | undefined
    ) => void;
    isDataCategoryLoading: boolean;
    dataCategoriesData: SelectableDataCategoryItem[] | undefined;
    onSubProcessorDataCategoryChange: (chosenCategories: Array<DataCategoryDataTypes>, dataProcessorId: string, subprocessorIndex: number) => void;
    key?: React.Key | null;
    onSubProcessorTransferImpactAssessmentChange?: (tia: string | null, dataProcessorId: string, index: number) => void;
    countriesLoading?: boolean;
    countryList?: Array<DotLegalSelectOption>;
    onSubProcessorStorageLocationChange?: (
        storageLocations: Array<string>,
        supportLocations: Array<string>,
        dataProcessorId: string,
        subprocessorIndex: number,
        dataContainedInThirdCountry: boolean
    ) => void;
    thirdCountries: Array<string>;
    onNoteChange: (note: string, dataProcessor: string, index: number) => void;
}

function SubDataProcessor(props: IDataProcessorsStep) {
    const { translateString } = useTranslation();
    const styles = useDataProcessorsStepStyles();

    function getSubProcessorItems(): Array<IDotLegalRemovableItem> {
        // A sub processor is only removable from the side of the sharing it was created on, and never when the
        // data processor is system generated or the step is read only.
        const canRemove =
            !props.dataProcessor.isSystemGenerated &&
            !props.dataProcessor.readOnly &&
            !props.isReadOnly &&
            props.groupEntityIsDataProcessor === props.dataProcessor.createdAsGroupEntityIsDataProcessor;

        return (props.subProcessors ?? []).map((subProcessor, index) => ({
            key: index.toString(),
            content: <SubProcessorComponent subProcessor={subProcessor} subProcessorIndex={index} {...props} />,
            removeTooltip: translateString("removeSubProcessor"),
            onRemove: canRemove ? () => props.onRemoveSubDataProcessor?.(props.dataProcessor.id, index) : undefined,
        }));
    }

    return (
        <React.Fragment>
            <Box marginBottom={3}>
                <DotLegalRemovableItems items={getSubProcessorItems()} />
            </Box>

            {props.canAddNewSubDataProcessor && (
                <Box sx={styles.addNewProcess}>
                    <DotLegalButton buttonType="secondary" disabled={props.readonly} onClick={() => props.onAddNewSubprocessorClick(false)}>
                        {props.subProcessors?.length > 0 ? translateString("addAnotherSubProcessor") : translateString("addSubProcessor")}
                    </DotLegalButton>

                    <DotLegalButton buttonType="secondary" disabled={props.readonly} onClick={() => props.onAddNewSubprocessorClick(true)}>
                        {translateString("insertLinkToSubProcessors")}
                    </DotLegalButton>
                </Box>
            )}
        </React.Fragment>
    );
}

export default SubDataProcessor;
